import { observer } from 'mobx-react-lite';
import { useState, useEffect } from 'react';
import { serverStore } from '../stores/server.store';
import { userStore } from '../stores/user.store';
import { AlertTriangle, X, Clock } from 'lucide-react';
import { Button } from './design/Button';
import { apiFetch } from '../utils/api';
import { can } from '@/utils/permissions';

export const ScheduledRestartBanner = observer(() => {
    const { activeServer } = serverStore;
    const { currentUser } = userStore;
    const [timeRemaining, setTimeRemaining] = useState<string>('');
    const [showBanner, setShowBanner] = useState(false);

    useEffect(() => {
        if (!activeServer || !activeServer.scheduled_restarts?.enabled || !activeServer.scheduled_restarts?.schedules) {
            setShowBanner(false);
            return;
        }

        // Don't show banner if server is not online
        if (activeServer.status !== 'online') {
            setShowBanner(false);
            return;
        }

        const schedules = activeServer.scheduled_restarts.schedules;
        if (schedules.length === 0) {
            setShowBanner(false);
            return;
        }

        // Check if restart is skipped (New logic: match exact timestamp)
        // We can't check purely "is skipped" here easily because we need to know WHICH restart is next first.
        // So we move the check INTO the interval loop or calculate nearest first.
        // Let's do it inside the interval to be accurate.
        // REMOVED old logic here.

        const interval = setInterval(() => {
            const now = new Date();
            let nearestRestart: Date | null = null;
            let nearestMs = Infinity;

            for (const scheduleTime of schedules) {
                const [hours, minutes] = scheduleTime.split(':').map(Number);
                const scheduledDate = new Date();
                scheduledDate.setHours(hours, minutes, 0, 0);

                // If time has passed today, schedule for tomorrow
                if (scheduledDate <= now) {
                    scheduledDate.setDate(scheduledDate.getDate() + 1);
                }

                const remainingMs = scheduledDate.getTime() - now.getTime();
                
                // Check if this specific restart is skipped
                const skippedList = activeServer.scheduled_restarts_skipped || [];
                const schedTimestamp = scheduledDate.getTime();
                const isSkipped = skippedList.some((ts: string) => {
                    const skipTime = new Date(ts).getTime();
                    return Math.abs(skipTime - schedTimestamp) < 60000;
                });

                // Only show banner if restart is within next 30 minutes AND NOT SKIPPED
                if (!isSkipped && remainingMs > 0 && remainingMs <= 30 * 60 * 1000 && remainingMs < nearestMs) {
                    nearestMs = remainingMs;
                    nearestRestart = scheduledDate;
                }
            }

            if (nearestRestart) {
                const minutes = Math.floor(nearestMs / 60000);
                const seconds = Math.floor((nearestMs % 60000) / 1000);
                
                if (minutes > 0) {
                    setTimeRemaining(`${minutes} Minute${minutes !== 1 ? 'n' : ''}`);
                } else {
                    setTimeRemaining(`${seconds} Sekunde${seconds !== 1 ? 'n' : ''}`);
                }
                setShowBanner(true);
            } else {
                setShowBanner(false);
            }
        }, 1000);

        return () => clearInterval(interval);
    }, [activeServer]);

    const handleSkipRestart = async () => {
        if (!activeServer) return;
        
        try {
            const schedules = activeServer.scheduled_restarts?.schedules || [];
            const now = new Date();
            let nearestRestart: Date | null = null;
            let nearestMs = Infinity;

            for (const scheduleTime of schedules) {
                const [hours, minutes] = scheduleTime.split(':').map(Number);
                const scheduledDate = new Date();
                scheduledDate.setHours(hours, minutes, 0, 0);
                if (scheduledDate <= now) scheduledDate.setDate(scheduledDate.getDate() + 1);
                
                const remainingMs = scheduledDate.getTime() - now.getTime();
                
                // Check if this restart is already skipped
                const skippedList = activeServer.scheduled_restarts_skipped || [];
                const schedTimestamp = scheduledDate.getTime();
                const isSkipped = skippedList.some((ts: string) => {
                    const skipTime = new Date(ts).getTime();
                    return Math.abs(skipTime - schedTimestamp) < 60000;
                });

                if (!isSkipped && remainingMs > 0 && remainingMs < nearestMs) {
                    nearestMs = remainingMs;
                    nearestRestart = scheduledDate;
                }
            }

            if (!nearestRestart) return;

            const response = await apiFetch(`/api/servers/${activeServer.id}/scheduled-restart/skip`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ targetTimestamp: nearestRestart.toISOString() })
            });

            const data = await response.json();
            
            if (response.ok && data.success) {
                setShowBanner(false);
                await serverStore.fetchData();
            } else {
                console.error('[Skip] Failed:', data.message || 'Unknown error');
            }
        } catch (error) {
            console.error('[Skip] Request failed:', error);
        }
    };

    const handleCancelSchedule = async () => {
        if (!activeServer) return;
        
        try {
            await serverStore.handleSaveServer({
                id: activeServer.id,
                scheduled_restarts: {
                    enabled: false,
                    schedules: activeServer.scheduled_restarts?.schedules || [],
                    warnings: activeServer.scheduled_restarts?.warnings || []
                }
            });
            
            setShowBanner(false);
        } catch (error) {
            console.error('Failed to cancel schedule:', error);
        }
    };

    if (!showBanner || !activeServer) return null;

    const canManage = can(currentUser, 'can_manage_schedules');

    return (
        <div className="fixed top-0 left-0 right-0 z-50 bg-gradient-to-r from-yellow-500/20 to-orange-500/20 border-b border-yellow-500/30 shadow-lg">
            <div className="container mx-auto px-4 py-3">
                <div className="flex items-center justify-between">
                    <div className="flex items-center space-x-3">
                        <div className="p-2 bg-yellow-500/20 rounded-lg border border-yellow-500/30">
                            <Clock size={20} className="text-yellow-400 animate-pulse" />
                        </div>
                        <div>
                            <div className="flex items-center space-x-2">
                                <AlertTriangle size={16} className="text-yellow-400" />
                                <span className="text-sm font-bold text-yellow-100">
                                    Geplanter Neustart
                                </span>
                            </div>
                            <p className="text-xs text-fg mt-0.5">
                                Server <span className="font-semibold text-white">{activeServer.name}</span> startet in{' '}
                                <span className="font-bold text-yellow-300">{timeRemaining}</span> neu.
                            </p>
                        </div>
                    </div>

                    {canManage && (
                        <div className="flex items-center space-x-2">
                            <Button
                                variant="ghost"
                                size="sm"
                                onClick={handleSkipRestart}
                                className="text-xs bg-yellow-500/10 hover:bg-yellow-500/20 text-yellow-300 border border-yellow-500/30"
                            >
                                <X size={14} className="mr-1" />
                                Überspringen
                            </Button>
                            <Button
                                variant="ghost"
                                size="sm"
                                onClick={handleCancelSchedule}
                                className="text-xs bg-red-500/10 hover:bg-red-500/20 text-red-300 border border-red-500/30"
                            >
                                <X size={14} className="mr-1" />
                                Komplett Deaktivieren
                            </Button>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
});
